import { useState } from 'react'
import {
  Card, Button, Table, Badge, Modal, Form, Row, Col, Spinner, Alert, Image,
} from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import api from '../../services/api'

const MODOS = [
  { value: 'siempre', label: 'Siempre (catálogo / default)' },
  { value: 'rango_absoluto', label: 'Promoción: rango de fechas' },
  { value: 'anual_md', label: 'Cada año: rango mes/día' },
  { value: 'anual_alrededor', label: 'Cada año: alrededor de un día' },
  { value: 'anual_nth_weekday', label: 'Cada año: n-ésimo día de la semana' },
]

const emptyForm = () => ({
  codigo: '',
  titulo: '',
  subtitulo: '',
  accent_color: '#0f6b5c',
  activo: true,
  es_default: false,
  prioridad: 100,
  modo_fecha: 'siempre',
  fecha_inicio: '',
  fecha_fin: '',
  mes_inicio: '',
  dia_inicio: '',
  mes_fin: '',
  dia_fin: '',
  mes: '',
  dia: '',
  pad_dias: 2,
  weekday: '',
  nth: '',
  imagenFile: null,
})

const isStaffAdmin = (user) =>
  !!user && (user.is_superuser || ['admin', 'superadmin'].includes(user.role))

const LoginCampaignsAdmin = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const allowed = isStaffAdmin(user)
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(emptyForm())
  const [preview, setPreview] = useState(null)

  const { data, isLoading, error } = useQuery(
    'login-campaigns',
    async () => {
      const response = await api.get('/auth/login-campaigns/')
      return response.data.results || response.data || []
    },
    { enabled: allowed }
  )

  const campaigns = data || []

  const saveMutation = useMutation(
    async () => {
      const fd = new FormData()
      const scalar = [
        'codigo', 'titulo', 'subtitulo', 'accent_color', 'prioridad', 'modo_fecha',
        'fecha_inicio', 'fecha_fin',
        'mes_inicio', 'dia_inicio', 'mes_fin', 'dia_fin',
        'mes', 'dia', 'pad_dias', 'weekday', 'nth',
      ]
      scalar.forEach((key) => {
        const v = form[key]
        if (v === '' || v === null || v === undefined) return
        fd.append(key, v)
      })
      fd.append('activo', form.activo ? 'true' : 'false')
      fd.append('es_default', form.es_default ? 'true' : 'false')
      if (form.imagenFile) {
        fd.append('imagen', form.imagenFile)
      }
      if (editing) {
        return api.patch(`/auth/login-campaigns/${editing.id}/`, fd)
      }
      return api.post('/auth/login-campaigns/', fd)
    },
    {
      onSuccess: () => {
        toast.success(editing ? 'Campaña actualizada' : 'Campaña creada')
        queryClient.invalidateQueries('login-campaigns')
        handleClose()
      },
      onError: (err) => {
        const d = err.response?.data
        const msg =
          (typeof d === 'object' && d !== null
            ? Object.entries(d).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' · ')
            : null) ||
          err.response?.data?.detail ||
          'Error al guardar'
        toast.error(msg)
      },
    }
  )

  const deleteMutation = useMutation(
    async (id) => api.delete(`/auth/login-campaigns/${id}/`),
    {
      onSuccess: () => {
        toast.success('Campaña eliminada')
        queryClient.invalidateQueries('login-campaigns')
      },
      onError: () => toast.error('No se pudo eliminar'),
    }
  )

  const handleOpen = (camp = null) => {
    if (camp) {
      setEditing(camp)
      setForm({
        ...emptyForm(),
        codigo: camp.codigo || '',
        titulo: camp.titulo || '',
        subtitulo: camp.subtitulo || '',
        accent_color: camp.accent_color || '#0f6b5c',
        activo: !!camp.activo,
        es_default: !!camp.es_default,
        prioridad: camp.prioridad ?? 100,
        modo_fecha: camp.modo_fecha || 'siempre',
        fecha_inicio: camp.fecha_inicio || '',
        fecha_fin: camp.fecha_fin || '',
        mes_inicio: camp.mes_inicio ?? '',
        dia_inicio: camp.dia_inicio ?? '',
        mes_fin: camp.mes_fin ?? '',
        dia_fin: camp.dia_fin ?? '',
        mes: camp.mes ?? '',
        dia: camp.dia ?? '',
        pad_dias: camp.pad_dias ?? 2,
        weekday: camp.weekday ?? '',
        nth: camp.nth ?? '',
        imagenFile: null,
      })
      setPreview(camp.imagen_url || null)
    } else {
      setEditing(null)
      setForm(emptyForm())
      setPreview(null)
    }
    setShowModal(true)
  }

  const handleClose = () => {
    setShowModal(false)
    setEditing(null)
    setForm(emptyForm())
    setPreview(null)
  }

  const onFile = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setForm((f) => ({ ...f, imagenFile: file }))
    setPreview(URL.createObjectURL(file))
  }

  if (!allowed) {
    return <Alert variant="warning">Solo administradores pueden gestionar campañas de login.</Alert>
  }

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div>
          <strong>Campañas del login</strong>
          <div className="text-muted small">
            Imágenes en el sistema (MEDIA). Activa promociones por fechas sin tocar código.
          </div>
        </div>
        <Button size="sm" onClick={() => handleOpen()}>Nueva campaña</Button>
      </Card.Header>
      <Card.Body>
        {isLoading && <Spinner animation="border" size="sm" />}
        {error && <Alert variant="danger">Error al cargar campañas</Alert>}
        {!isLoading && (
          <Table responsive hover size="sm">
            <thead>
              <tr>
                <th>Vista</th>
                <th>Código</th>
                <th>Título</th>
                <th>Vigencia</th>
                <th>Prioridad</th>
                <th>Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.id}>
                  <td style={{ width: 72 }}>
                    {c.imagen_url ? (
                      <Image src={c.imagen_url} thumbnail style={{ maxHeight: 48, maxWidth: 64 }} />
                    ) : (
                      '—'
                    )}
                  </td>
                  <td><code>{c.codigo}</code></td>
                  <td>{c.titulo}</td>
                  <td>
                    <small>{c.modo_fecha_display || c.modo_fecha}</small>
                    {c.modo_fecha === 'rango_absoluto' && (
                      <div className="text-muted small">{c.fecha_inicio} → {c.fecha_fin}</div>
                    )}
                  </td>
                  <td>{c.prioridad}</td>
                  <td>
                    {c.es_default && <Badge bg="info" className="me-1">Default</Badge>}
                    <Badge bg={c.activo ? 'success' : 'secondary'}>
                      {c.activo ? 'Activa' : 'Off'}
                    </Badge>
                  </td>
                  <td className="text-end">
                    <Button size="sm" variant="outline-primary" className="me-1" onClick={() => handleOpen(c)}>
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => {
                        if (window.confirm(`¿Eliminar "${c.codigo}"?`)) deleteMutation.mutate(c.id)
                      }}
                    >
                      Eliminar
                    </Button>
                  </td>
                </tr>
              ))}
              {campaigns.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-muted">
                    Sin campañas. Ejecuta <code>seed_login_campaigns</code> o crea una nueva.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </Card.Body>

      <Modal show={showModal} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Editar campaña' : 'Nueva campaña'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="g-2">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Código</Form.Label>
                  <Form.Control
                    value={form.codigo}
                    disabled={!!editing}
                    onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                    placeholder="promo_julio"
                  />
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group>
                  <Form.Label>Título</Form.Label>
                  <Form.Control
                    value={form.titulo}
                    onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Prioridad</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.prioridad}
                    onChange={(e) => setForm({ ...form, prioridad: e.target.value })}
                  />
                  <Form.Text>Menor = primero</Form.Text>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Subtítulo</Form.Label>
                  <Form.Control
                    value={form.subtitulo}
                    onChange={(e) => setForm({ ...form, subtitulo: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Color acento</Form.Label>
                  <Form.Control
                    type="color"
                    value={form.accent_color}
                    onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Modo de vigencia</Form.Label>
                  <Form.Select
                    value={form.modo_fecha}
                    onChange={(e) => setForm({ ...form, modo_fecha: e.target.value })}
                  >
                    {MODOS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4} className="d-flex align-items-end gap-3">
                <Form.Check
                  type="switch"
                  label="Activo"
                  checked={form.activo}
                  onChange={(e) => setForm({ ...form, activo: e.target.checked })}
                />
                <Form.Check
                  type="switch"
                  label="Es default"
                  checked={form.es_default}
                  onChange={(e) => setForm({ ...form, es_default: e.target.checked })}
                />
              </Col>

              {form.modo_fecha === 'rango_absoluto' && (
                <>
                  <Col md={6}>
                    <Form.Label>Fecha inicio</Form.Label>
                    <Form.Control
                      type="date"
                      value={form.fecha_inicio}
                      onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Fecha fin</Form.Label>
                    <Form.Control
                      type="date"
                      value={form.fecha_fin}
                      onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })}
                    />
                  </Col>
                </>
              )}

              {form.modo_fecha === 'anual_md' && (
                <>
                  <Col md={3}>
                    <Form.Label>Mes inicio</Form.Label>
                    <Form.Control type="number" min={1} max={12} value={form.mes_inicio}
                      onChange={(e) => setForm({ ...form, mes_inicio: e.target.value })} />
                  </Col>
                  <Col md={3}>
                    <Form.Label>Día inicio</Form.Label>
                    <Form.Control type="number" min={1} max={31} value={form.dia_inicio}
                      onChange={(e) => setForm({ ...form, dia_inicio: e.target.value })} />
                  </Col>
                  <Col md={3}>
                    <Form.Label>Mes fin</Form.Label>
                    <Form.Control type="number" min={1} max={12} value={form.mes_fin}
                      onChange={(e) => setForm({ ...form, mes_fin: e.target.value })} />
                  </Col>
                  <Col md={3}>
                    <Form.Label>Día fin</Form.Label>
                    <Form.Control type="number" min={1} max={31} value={form.dia_fin}
                      onChange={(e) => setForm({ ...form, dia_fin: e.target.value })} />
                  </Col>
                </>
              )}

              {(form.modo_fecha === 'anual_alrededor' || form.modo_fecha === 'anual_nth_weekday') && (
                <>
                  <Col md={3}>
                    <Form.Label>Mes</Form.Label>
                    <Form.Control type="number" min={1} max={12} value={form.mes}
                      onChange={(e) => setForm({ ...form, mes: e.target.value })} />
                  </Col>
                  {form.modo_fecha === 'anual_alrededor' && (
                    <Col md={3}>
                      <Form.Label>Día</Form.Label>
                      <Form.Control type="number" min={1} max={31} value={form.dia}
                        onChange={(e) => setForm({ ...form, dia: e.target.value })} />
                    </Col>
                  )}
                  {form.modo_fecha === 'anual_nth_weekday' && (
                    <>
                      <Col md={3}>
                        <Form.Label>Día semana (0=dom)</Form.Label>
                        <Form.Control type="number" min={0} max={6} value={form.weekday}
                          onChange={(e) => setForm({ ...form, weekday: e.target.value })} />
                      </Col>
                      <Col md={3}>
                        <Form.Label>N-ésimo (o -1)</Form.Label>
                        <Form.Control type="number" value={form.nth}
                          onChange={(e) => setForm({ ...form, nth: e.target.value })} />
                      </Col>
                    </>
                  )}
                  <Col md={3}>
                    <Form.Label>Pad días</Form.Label>
                    <Form.Control type="number" min={0} value={form.pad_dias}
                      onChange={(e) => setForm({ ...form, pad_dias: e.target.value })} />
                  </Col>
                </>
              )}

              <Col md={8}>
                <Form.Group>
                  <Form.Label>Imagen {editing ? '(opcional al editar)' : ''}</Form.Label>
                  <Form.Control type="file" accept="image/*" onChange={onFile} />
                </Form.Group>
              </Col>
              <Col md={4} className="d-flex align-items-center">
                {preview && (
                  <Image src={preview} thumbnail style={{ maxHeight: 80 }} />
                )}
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>Cancelar</Button>
          <Button
            variant="primary"
            disabled={saveMutation.isLoading || !form.codigo || !form.titulo || (!editing && !form.imagenFile)}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isLoading ? 'Guardando…' : 'Guardar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  )
}

export default LoginCampaignsAdmin
